import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { createForm, useCreateFormStore } from '@components/form';
import {
  PasswordStrengthBar,
  usePasswordValidations,
} from '@components/password-strength-bar/password-strength-bar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useSetCompanyUserInfo } from 'api/users/hooks';
import { SetCompanyUserInfoData } from 'api/users/requests';
import { AuthenticationPageContainer, Title } from 'ui/unauthenticated/pages/common';

import { routePaths } from '../navigation';

const Form = createForm<SetCompanyUserInfoData>();

export const CompanyUserSetInfoPage = () => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const navigate = useNavigate();
  const formValues = formStore.useFormValues();
  const password = (formValues['password'] as string) ?? '';
  const { isAllValid, ValidationMessages } = usePasswordValidations(password);

  const handleSignupSuccess = () => {
    navigate(routePaths.companyGetStarted);
  };

  const signupMutation = useSetCompanyUserInfo({ onSuccess: handleSignupSuccess });

  const handleContinue = (data: SetCompanyUserInfoData) => {
    signupMutation.mutate(data);
  };

  return (
    <Container>
      <AuthenticationPageContainer>
        <Stack space={6} align="center" justify="center">
          <Title>{t('title')}</Title>

          <Form onSubmit={handleContinue} store={formStore}>
            <Stack space={6}>
              <Form.TextField
                id="name"
                label={t('core.name')}
                validators={['required']}
                hideRequiredIndicator
                maxLength={255}
              />
              <PasswordContainer>
                <Stack space={2}>
                  <Form.PasswordField id="password" label={t('core.password')} />
                  <PasswordStrengthBar password={password} />
                </Stack>
                <ValidationMessages />
              </PasswordContainer>

              <Form.Submit
                variant="primary-blue"
                text={t('core.continue')}
                isDisabled={!isAllValid || !formValues['name'] || signupMutation.isLoading}
              />
            </Stack>
          </Form>
        </Stack>
      </AuthenticationPageContainer>
    </Container>
  );
};

const Container = styled.div({
  form: {
    width: '100%',
  },
});

const PasswordContainer = styled.div({
  padding: pxToRem(16),
  borderRadius: pxToRem(8),
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
});

const locale = {
  en: {
    title: 'Create your profile',
    workEmail: 'Work email',
    disclaimer: 'By creating an account, you agree to the {terms} and {privacy}.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    signupMicrosoft: 'Sign up with Microsoft',
    or: 'or',
    existing: 'Already have an account? Sign in',
  },
};
